// frontend/src/pages/org/org-customers-page.tsx
// Title Case buttons; "Enabled"/"Disabled"; no em dashes; no period in heading.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from '@tanstack/react-form';
import { Info, Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { FieldError } from '@/components/form/field-error';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFetch } from '@/hooks/use-fetch';
import { useWorkspace } from '@/contexts/workspace-context';
import { useSession } from '@/contexts/session-context';
import { parseApiError } from '@/lib/api-error';
import { qk } from '@/lib/query/keys';
import { workspaceNameSchema } from '@/lib/form/schemas';
import { PageContainer } from '@/components/page-container';
import { OrgWorkspaceTable } from './org-workspace-table';
import type { OrgWorkspaceRow } from './org-workspace-table';

/**
 * Org Customers page: table of every customer workspace (kind='customer').
 * Shares the OrgWorkspaceTable renderer with OrgWorkspacesPage (kind='team').
 * Enable/Disable All routes through the shared table which sends kind:'customer'
 * in the POST body automatically.
 */
export function OrgCustomersPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const fetchWithAuth = useFetch();
  const { refreshWorkspaces } = useWorkspace();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<OrgWorkspaceRow[]>([]);
  // AIT-436: names the Usage column after the org's own meter.
  const [usageUnit, setUsageUnit] = useState<'messages' | 'actions'>('messages');
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const { role } = useSession();
  // Admins get the deprecated rows in the payload; the table hides them behind
  // its own Show deprecated switch (and disables it when there are none).
  const includeDeprecated = role === 'admin';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const tokens: string[] = [row.name];
      for (const c of row.channels) {
        if (c.name) tokens.push(c.name);
        if (c.username) tokens.push(c.username);
        if (c.phone) {
          tokens.push(c.phone);
          tokens.push(c.phone.replace(/[+\s-]/g, ''));
        }
      }
      return tokens.join(' ').toLowerCase().includes(q);
    });
  }, [rows, search]);

  const load = useCallback(async () => {
    if (!orgId) return;
    const res = await fetchWithAuth(
      `/organizations/${orgId}/workspaces?kind=customer${includeDeprecated ? '&includeDeprecated=true' : ''}`,
    );
    if (res.ok) {
      const data = (await res.json()) as {
        workspaces: OrgWorkspaceRow[];
        usageUnit?: 'messages' | 'actions';
      };
      setRows(data.workspaces);
      setUsageUnit(data.usageUnit === 'actions' ? 'actions' : 'messages');
    } else {
      const body = await res.json().catch(() => null);
      toast.error(parseApiError(res.status, body, 'Failed to load customers').userMessage);
    }
    setLoaded(true);
  }, [orgId, fetchWithAuth, includeDeprecated]);

  useEffect(() => {
    // Async IIFE so the analyzer sees the setState happen after an await, not
    // synchronously in the effect body (react-hooks/set-state-in-effect).
    // Same pattern as org-team-page.tsx / org-layout.tsx / session-context.tsx.
    void (async () => {
      await load();
    })();
  }, [load]);

  const form = useForm({
    defaultValues: { name: '', externalId: '' },
    onSubmit: async ({ value }) => {
      if (!orgId) return;
      setCreating(true);
      try {
        const res = await fetchWithAuth(`/organizations/${orgId}/customers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: value.name.trim(),
            ...(value.externalId.trim() ? { externalId: value.externalId.trim() } : {}),
          }),
        });
        if (res.ok) {
          toast.success('Customer created');
          setDialogOpen(false);
          form.reset();
          // Refresh every list that should show the new customer: this table,
          // the app-wide workspace list (so navigating into the new customer
          // doesn't bounce off WorkspaceLayout's stale-list check), and the
          // TanStack-cached org lists (connect-modal picker).
          await Promise.all([
            load(),
            refreshWorkspaces(),
            queryClient.invalidateQueries({ queryKey: qk.org(orgId, 'workspaces') }),
          ]);
        } else {
          const body = await res.json().catch(() => null);
          toast.error(parseApiError(res.status, body, 'Failed to create customer').userMessage);
        }
      } finally {
        setCreating(false);
      }
    },
  });

  return (
    <PageContainer variant="fill" className="gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Customers</h1>
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            if (!open) form.reset();
            setDialogOpen(open);
          }}
        >
          <DialogTrigger
            render={
              <Button size="sm">
                <Plus className="size-3.5" />
                New Customer
              </Button>
            }
          />
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New Customer</DialogTitle>
            </DialogHeader>
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit();
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="new-customer-name">Customer Name</Label>
                <form.Field
                  name="name"
                  validators={{ onChange: workspaceNameSchema, onSubmit: workspaceNameSchema }}
                >
                  {(field) => (
                    <>
                      <Input
                        id="new-customer-name"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        placeholder="Acme Cafe"
                        autoFocus
                        aria-invalid={field.state.meta.errors.length > 0 || undefined}
                        aria-describedby={
                          field.state.meta.errors.length > 0
                            ? 'new-customer-name-error'
                            : undefined
                        }
                      />
                      <FieldError
                        id="new-customer-name-error"
                        errors={field.state.meta.errors}
                      />
                    </>
                  )}
                </form.Field>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-customer-external-id">External ID</Label>
                <form.Field name="externalId">
                  {(field) => (
                    <Input
                      id="new-customer-external-id"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="crm-123"
                      maxLength={120}
                      aria-describedby="new-customer-external-id-hint"
                    />
                  )}
                </form.Field>
                <p id="new-customer-external-id-hint" className="text-xs text-muted-foreground">
                  Optional. A unique identifier used in your external systems.
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={creating}
                  onClick={() => {
                    form.reset();
                    setDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <form.Subscribe
                  selector={(s) => [s.canSubmit, s.isSubmitting, s.values.name] as const}
                >
                  {([canSubmit, isSubmitting, name]) => (
                    <Button
                      type="submit"
                      disabled={!canSubmit || isSubmitting || creating || !name.trim()}
                    >
                      {creating && <Loader2 className="size-3.5 animate-spin" />}
                      Create Customer
                    </Button>
                  )}
                </form.Subscribe>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loaded && rows.length === 0 && (
        <Alert className="border-blue-500/30 bg-blue-500/5">
          <Info className="text-blue-500" />
          <AlertDescription>
            No customers yet. Create one here or share an onboarding link; connected customers appear in this list.
          </AlertDescription>
        </Alert>
      )}

      {rows.length > 0 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Customer or channel"
            className="h-9 pl-8 text-sm"
            aria-label="Search customers"
          />
        </div>
      )}

      {rows.length > 0 && (
        <OrgWorkspaceTable rows={filtered} usageUnit={usageUnit} kind="customer" onRefresh={load} />
      )}
    </PageContainer>
  );
}
